<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :show-close-button="true"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :title="popupTitle"
    width="min(1100px, 96vw)"
    height="min(720px, 92vh)"
    @hiding="onClose"
  >
    <div class="work-interval-price-popup">
      <GridPanelHeader
        :title="''"
        :grid-ref="$refs.priceGrid"
        :show-add="true"
        :show-filter="false"
        :show-export="false"
        :show-columns="false"
        :no-bottom-margin="true"
        @add="addPriceRow"
      />
      <div v-if="conflictRowKeys.length" class="work-interval-price-popup__validation-message">
        {{ $t("works.work_group_list.overlapping_price_intervals") }}
      </div>
      <DxDataGrid
        ref="priceGrid"
        :data-source="priceDataSource"
        :remote-operations="false"
        :show-borders="true"
        :show-column-lines="true"
        :row-alternation-enabled="true"
        :column-auto-width="false"
        :element-attr="{ class: 'smagria-grid work-interval-price-grid' }"
        @init-new-row="onInitNewRow"
        @row-prepared="onRowPrepared"
        @row-updating="onRowUpdating"
      >
        <DxColumn
          data-field="date_from"
          data-type="date"
          :format="getDxGridDefaultDateFormat"
          :caption="$t('works.work_group_list.date_from')"
          :allow-filtering="true"
          :filter-operations="['=', '<', '<=', '>', '>=']"
        >
          <DxRequiredRule :message="$t('validation.field_is_required')" />
        </DxColumn>
        <DxColumn
          data-field="date_to"
          data-type="date"
          :format="getDxGridDefaultDateFormat"
          :caption="$t('works.work_group_list.date_to')"
          :allow-filtering="true"
          :filter-operations="['=', '<', '<=', '>', '>=']"
        >
          <DxRequiredRule :message="$t('validation.field_is_required')" />
        </DxColumn>
        <DxColumn
          data-field="unit_price"
          data-type="number"
          :format="getDxGridDefaultNumberFormat"
          :caption="$t('common.unit_price') + ' / ha'"
          :allow-filtering="true"
          :filter-operations="['=', '<', '<=', '>', '>=']"
        >
          <DxRequiredRule :message="$t('validation.field_is_required')" />
        </DxColumn>
        <DxColumn
          data-field="informative"
          data-type="boolean"
          :caption="$t('works.work_group_list.informative_price')"
          :set-cell-value="setInformativeValue"
          :allow-filtering="true"
          :filter-operations="['=']"
          :width="130"
        >
          <DxLookup :data-source="informativeOptions" value-expr="value" display-expr="name" />
        </DxColumn>
        <DxColumn
          data-field="department_uuids"
          :caption="$t('works.work_group_list.departments')"
          cell-template="departmentCellTemplate"
          edit-cell-template="departmentEditTemplate"
          :calculate-filter-expression="calculateDepartmentFilterExpression"
          :allow-filtering="true"
          :filter-operations="['contains']"
          :min-width="260"
        >
          <DxCustomRule
            :validation-callback="validateDepartmentSelection"
            :message="$t('works.work_group_list.department_required')"
            :reevaluate="true"
          />
        </DxColumn>
        <template #departmentCellTemplate="{ data }">
          <span>{{ getDepartmentNames(data.data) }}</span>
        </template>
        <template #departmentEditTemplate="{ data }">
          <DxTagBox
            :value="data.value || []"
            :data-source="departments"
            value-expr="uuid"
            display-expr="name"
            :search-enabled="true"
            :show-selection-controls="true"
            :disabled="Boolean(data.row.data.informative)"
            apply-value-mode="instantly"
            @value-changed="data.setValue($event.value)"
          />
        </template>
        <DxEditing
          mode="form"
          :allow-adding="false"
          :allow-updating="true"
          :allow-deleting="true"
          :use-icons="true"
        >
          <DxTexts
            :add-row="$t('common.add_new')"
            :edit-row="$t('common.edit')"
            :delete-row="$t('common.delete')"
            :save-row-changes="$t('common.save')"
            :cancel-row-changes="$t('common.cancel')"
            :confirm-delete-title="$t('common.confirm_delete_title')"
            :confirm-delete-message="$t('common.confirm_delete', { title: $t('common.unit_price') })"
          />
        </DxEditing>
        <DxLoadPanel :enabled="true" :show-indicator="true" :show-pane="true" />
        <DxFilterRow :visible="true" apply-filter="auto" />
        <DxHeaderFilter :visible="false" />
        <DxPaging :enabled="false" />
        <DxScrolling mode="standard" show-scrollbar="always" />
      </DxDataGrid>

      <div class="work-interval-price-popup__actions">
        <SmagriaButton
          :label="$t('common.save')"
          :disabled="loading"
          :action="onSave"
          :customClass="'primary'"
          :width="'150px'"
        />
        <SmagriaButton
          :label="$t('common.cancel')"
          :disabled="loading"
          :action="onClose"
          :customClass="'secondary border margin-left'"
          :width="'130px'"
        />
      </div>
    </div>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxPopup from "devextreme-vue/popup";
import DxTagBox from "devextreme-vue/tag-box";
import DxDataGrid, {
  DxColumn,
  DxCustomRule,
  DxEditing,
  DxFilterRow,
  DxHeaderFilter,
  DxLoadPanel,
  DxLookup,
  DxPaging,
  DxRequiredRule,
  DxScrolling,
  DxTexts,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { format } from "date-fns";
import { errorMessage, successMessage } from "@/helpers/common";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "WorkDateIntervalUnitPricePopup",
  components: {
    DxPopup,
    DxTagBox,
    DxDataGrid,
    DxColumn,
    DxCustomRule,
    DxEditing,
    DxFilterRow,
    DxHeaderFilter,
    DxLoadPanel,
    DxLookup,
    DxPaging,
    DxRequiredRule,
    DxScrolling,
    DxTexts,
    SmagriaButton,
    GridPanelHeader,
  },
  props: {
    visible: { type: Boolean, default: false },
    work: { type: Object, required: true },
  },
  data() {
    return {
      rows: [],
      loading: false,
      nextLocalKey: 1,
      pricesLoaded: false,
      conflictRowKeys: [],
      priceDataSource: new DataSource({
        store: new CustomStore({
          key: "local_key",
          load: () => this.loadData(),
          insert: (values) => this.insertPrice(values),
          update: (key, values) => this.updatePrice(key, values),
          remove: (key) => this.removePrice(key),
        }),
      }),
    };
  },
  computed: {
    ...mapGetters({
      departments: "company/departments",
      company: "auth/domain",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      getDxGridDefaultNumberFormat: "settings/getDxGridDefaultNumberFormat",
    }),
    popupTitle() {
      return `${this.$t("works.work_group_list.interval_unit_prices")}: ${this.work.name}`;
    },
    informativeOptions() {
      return [
        { value: true, name: this.$t("common.yes") },
        { value: false, name: this.$t("common.no") },
      ];
    },
  },
  methods: {
    ...mapActions({
      fetchPrices: "works-manager/getWorkDateIntervalUnitPrices",
      syncPrices: "works-manager/syncWorkDateIntervalUnitPrices",
      getDepartments: "company/getDepartments",
    }),
    async loadData() {
      if (this.pricesLoaded) {
        return this.rows;
      }

      this.loading = true;
      try {
        if (!this.departments.length && this.company?.uuid) {
          await this.getDepartments({ company_uuid: this.company.uuid });
        }
        const response = await this.fetchPrices({ work_uuid: this.work.uuid });
        this.rows = (response || []).map((row) => ({
          ...row,
          date_from: row.date_from ? new Date(`${row.date_from}T00:00:00`) : null,
          date_to: row.date_to ? new Date(`${row.date_to}T00:00:00`) : null,
          local_key: row.uuid || `new-${this.nextLocalKey++}`,
        }));
        this.pricesLoaded = true;
        return this.rows;
      } catch (error) {
        errorMessage(this, error?.message || this.$t("works.work_group_list.price_load_error"));
        this.pricesLoaded = true;
        return [];
      } finally {
        this.loading = false;
      }
    },
    onInitNewRow(e) {
      e.data.local_key = `new-${this.nextLocalKey++}`;
      e.data.uuid = null;
      e.data.informative = false;
      e.data.department_uuids = [];
    },
    addPriceRow() {
      this.$refs.priceGrid?.instance?.addRow();
    },
    insertPrice(values) {
      this.clearConflictRows();
      const row = {
        ...values,
        local_key: values.local_key || `new-${this.nextLocalKey++}`,
        department_uuids: values.department_uuids || [],
      };
      this.rows.push(row);
      return row;
    },
    updatePrice(key, values) {
      this.clearConflictRows();
      const row = this.rows.find((item) => item.local_key === key);
      if (row) {
        Object.assign(row, values);
      }
      return key;
    },
    removePrice(key) {
      this.clearConflictRows();
      const index = this.rows.findIndex((item) => item.local_key === key);
      if (index >= 0) {
        this.rows.splice(index, 1);
      }
      return key;
    },
    onRowUpdating(e) {
      this.clearConflictRows();
      if (e.newData.informative === true) {
        e.newData.department_uuids = [];
      }
    },
    setInformativeValue(newData, value) {
      newData.informative = value;
      if (value) {
        newData.department_uuids = [];
      }
    },
    validateDepartmentSelection({ data, value }) {
      return Boolean(data?.informative) || (Array.isArray(value) && value.length > 0);
    },
    getDepartmentNames(row) {
      if (row.informative) return this.$t("works.work_group_list.all_departments_informative");
      const selected = new Set(row.department_uuids || []);
      return this.departments.filter((department) => selected.has(department.uuid)).map((department) => department.name).join(", ");
    },
    calculateDepartmentFilterExpression(filterValue) {
      const search = String(filterValue || "").toLowerCase();
      return (row) => this.getDepartmentNames(row).toLowerCase().includes(search);
    },
    onRowPrepared(e) {
      if (e.rowType !== "data") return;
      e.rowElement.classList.toggle(
        "work-interval-price-row--error",
        this.conflictRowKeys.includes(e.data.local_key),
      );
    },
    clearConflictRows() {
      if (!this.conflictRowKeys.length) return;
      this.conflictRowKeys = [];
      this.$nextTick(() => this.$refs.priceGrid?.instance?.repaint());
    },
    setConflictRows(keys) {
      this.conflictRowKeys = Array.from(new Set(keys));
      this.$nextTick(() => this.$refs.priceGrid?.instance?.repaint());
    },
    async onSave() {
      const grid = this.$refs.priceGrid?.instance;
      if (grid?.hasEditData()) {
        await grid.saveEditData();
        if (grid.hasEditData()) return;
      }

      const validationError = this.validateRows();
      if (validationError) {
        errorMessage(this, validationError);
        return;
      }

      this.loading = true;
      try {
        await this.syncPrices({
          work_uuid: this.work.uuid,
          items: this.rows.map((row) => ({
            uuid: row.uuid || null,
            date_from: this.toDateString(row.date_from),
            date_to: this.toDateString(row.date_to),
            unit_price: Number(row.unit_price),
            informative: Boolean(row.informative),
            department_uuids: row.informative ? [] : row.department_uuids,
          })),
        });
        successMessage(this, this.$t("works.work_group_list.price_save_success"));
        this.$emit("save");
      } catch (error) {
        errorMessage(this, error?.message || this.$t("works.work_group_list.price_save_error"));
      } finally {
        this.loading = false;
      }
    },
    validateRows() {
      this.setConflictRows([]);
      for (const row of this.rows) {
        if (!row.date_from || !row.date_to || row.unit_price === null || row.unit_price === undefined || Number(row.unit_price) < 0) {
          return this.$t("works.work_group_list.complete_all_price_rows");
        }
        if (this.toDateString(row.date_to) < this.toDateString(row.date_from)) {
          return this.$t("works.work_group_list.invalid_date_interval");
        }
        if (!row.informative && !(row.department_uuids || []).length) {
          return this.$t("works.work_group_list.department_required");
        }
      }

      const pricedRows = this.rows.filter((row) => !row.informative);
      const conflictingKeys = [];
      for (let index = 0; index < pricedRows.length; index++) {
        for (let otherIndex = index + 1; otherIndex < pricedRows.length; otherIndex++) {
          const row = pricedRows[index];
          const other = pricedRows[otherIndex];
          const sharesDepartment = (row.department_uuids || []).some((uuid) => (other.department_uuids || []).includes(uuid));
          const overlaps = this.toDateString(row.date_from) <= this.toDateString(other.date_to)
            && this.toDateString(other.date_from) <= this.toDateString(row.date_to);
          if (sharesDepartment && overlaps) {
            conflictingKeys.push(row.local_key, other.local_key);
          }
        }
      }
      if (conflictingKeys.length) {
        this.setConflictRows(conflictingKeys);
        return this.$t("works.work_group_list.overlapping_price_intervals");
      }
      return null;
    },
    toDateString(value) {
      return value ? format(value instanceof Date ? value : new Date(value), "yyyy-MM-dd") : null;
    },
    onClose() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/workDateIntervalUnitPrice.scss";
</style>